/**
 * useRKHSummary Hook
 * Custom hook for fetching and managing RKH summary data
 */

import { useState, useEffect, useCallback } from 'react';
import rkhApiService from '../services/rkhApiService';
import { RKHCompanyGroup, RKHSummaryFilters } from '../types';

interface UseRKHSummaryReturn {
    data: RKHCompanyGroup[];
    loading: boolean;
    error: string | null;
    fetchData: (filters?: RKHSummaryFilters) => Promise<void>;
}

export const useRKHSummary = (initialFilters?: RKHSummaryFilters): UseRKHSummaryReturn => {
    const [data, setData] = useState<RKHCompanyGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async (filters?: RKHSummaryFilters) => {
        setLoading(true);
        setError(null);

        try {
            const response = await rkhApiService.getRKHSummary(filters);
            setData(response.data);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch RKH summary';
            setError(errorMessage);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (initialFilters) {
            fetchData(initialFilters);
        }
    }, []);

    return {
        data,
        loading,
        error,
        fetchData,
    };
};
